<script setup lang="ts">
import { computed, ref } from 'vue';
import { uniqueId } from 'lodash-es';
import PlusOutlineIcon from '@/assets/icons/shared/plus-outline.svg';
import AddressCard from '@/modules/counterparty/counterparty-master/components/AddressCard.vue';
import AddressFormModal from '@/modules/counterparty/counterparty-master/components/AddressFormModal.vue';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import type { CounterpartyAddressItem } from '@/modules/counterparty/counterparty-master/types/address-form.ts';
import type { AddressFormData } from '@/modules/counterparty/api/address-api.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { useWizardOptionalStep } from '@/providers/wizard.ts';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiCardList from '@/modules/ui/components/ui/UiCardList.vue';

useWizardOptionalStep();

const { formData, submittedItemIds } = useCounterpartyMaster();

const isFormOpen = ref(false);
const editItem = ref<CounterpartyAddressItem | null>(null);

const extraTypes = computed<Option[]>(() =>
  formData.value.addresses.map((address) => ({
    code: address.type.code,
    label: address.type.label,
  }))
);

function openForm(item: CounterpartyAddressItem | null = null) {
  editItem.value = item;
  isFormOpen.value = true;
}

function onSubmit(data: AddressFormData) {
  if (editItem.value) {
    const editingId = editItem.value.id;

    submittedItemIds.delete(editingId);
    formData.value = {
      ...formData.value,
      addresses: formData.value.addresses.map((address) =>
        address.id === editingId
          ? {
              ...address,
              type: data.type,
              address: data.address,
              comment: data.comment,
            }
          : address
      ),
    };
    return;
  }

  formData.value = {
    ...formData.value,
    addresses: [
      ...formData.value.addresses,
      {
        id: uniqueId(IdPrefix.Control),
        type: data.type,
        address: data.address,
        comment: data.comment,
      },
    ],
  };
}

function removeAddress(id: string) {
  submittedItemIds.delete(id);
  formData.value = {
    ...formData.value,
    addresses: formData.value.addresses.filter((address) => address.id !== id),
  };
}
</script>

<template>
  <div class="step-address">
    <div
      class="list"
      :class="{ _empty: !formData.addresses.length }"
    >
      <UiCardList
        v-if="formData.addresses.length"
        class="cards"
        :list="formData.addresses"
        with-add
        equal-height
        @add="openForm()"
      >
        <template #card="{ item }">
          <AddressCard
            :item="item"
            @edit="openForm(item)"
            @remove="removeAddress(String(item.id))"
          />
        </template>
      </UiCardList>

      <UiButton
        v-else
        color="black"
        mode="outline"
        @click="openForm()"
      >
        <PlusOutlineIcon v-prefix />
        <span>{{ $t('counterparty.master.steps.address.add') }}</span>
      </UiButton>
    </div>

    <AddressFormModal
      v-model="isFormOpen"
      :edit-item="editItem"
      :extra-types="extraTypes"
      @submit="onSubmit"
    />
  </div>
</template>

<style scoped lang="scss">
.step-address {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
}

.list {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  border: var(--border-dashed-gray-400);
  border-radius: 8px;

  &._empty {
    justify-content: center;
    align-items: center;
  }
}

.cards {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
}

.list .cards {
  grid-template-columns: repeat(auto-fill, minmax(min(140px, 100%), 1fr));
}
</style>
